/**
 * SEO Configuration
 * 
 * Centralized metadata and SEO settings for all pages.
 */

export const SITE_CONFIG = {
  siteName: 'Canadian Document Services',
  siteUrl: 'https://canadiandocumentservices.com',
  defaultTitle: 'Canadian Document Services | Government Application Processing',
  defaultDescription: 'Professional administrative assistance for Record Suspension, U.S. Entry Waivers, and International Filings. Federally incorporated in Canada.',
  defaultImage: 'https://canadiandocumentservices.com/og-image.jpg',
  twitterHandle: '@CDSCanada',
  locale: 'en_CA',
};

/**
 * Page-specific SEO metadata
 */
export const PAGE_SEO = {
  home: {
    title: 'Canadian Document Services | Record Suspension & U.S. Entry Waivers',
    description: 'Professional administrative assistance for Record Suspension, U.S. Entry Waivers, and International Filings. Federally incorporated and operating nationally from Toronto.',
    canonical: '/',
  },
  recordSuspensions: {
    title: 'Record Suspension in Canada | Canadian Document Services',
    description: 'Administrative preparation for Record Suspension applications under the Criminal Records Act. Eligibility review, document coordination, and application submission assistance.',
    canonical: '/record-suspensions',
  },
  usEntryWaivers: {
    title: 'U.S. Entry Waivers | Cross-Border Travel Assistance | Canadian Document Services',
    description: 'Administrative preparation for U.S. Entry Waiver applications (Form I-192) for individuals deemed inadmissible to the United States. Expert document coordination.',
    canonical: '/us-entry-waivers',
  },
  internationalFilings: {
    title: 'International Filings & Dubai Documentation | Canadian Document Services',
    description: 'Criminal record authentication and document preparation for international requirements. Specialized UAE/Dubai police clearance certificate assistance.',
    canonical: '/international-filings',
  },
  eligibility: {
    title: 'Eligibility Assessment | Canadian Document Services',
    description: 'Complete a quick eligibility assessment to determine if you qualify for Record Suspension, U.S. Entry Waiver, or International Filing services.',
    canonical: '/eligibility',
  },
  contact: {
    title: 'Contact Us | Canadian Document Services',
    description: 'Contact Canadian Document Services for assistance with Record Suspension, U.S. Entry Waivers, and International Filings. Located in Toronto, serving all of Canada.',
    canonical: '/contact',
  },
  thankYou: {
    title: 'Assessment Received | Canadian Document Services',
    description: 'Thank you for submitting your eligibility assessment. Our team will review your submission and contact you within 1-2 business days.',
    canonical: '/thank-you',
    noIndex: true,
  },
  terms: {
    title: 'Terms & Conditions | Canadian Document Services',
    description: 'Terms and conditions for Canadian Document Services Inc. administrative assistance services.',
    canonical: '/terms',
  },
  privacy: {
    title: 'Privacy Policy | Canadian Document Services',
    description: 'Privacy policy for Canadian Document Services Inc. Learn how we collect, use, and protect your personal information.',
    canonical: '/privacy',
  },
  caseArchive: {
    title: 'Case Archive | Canadian Document Services',
    description: 'Browse anonymized case outcomes for Record Suspension and U.S. Entry Waivers. See real results from our administrative assistance services.',
    canonical: '/case-archive',
    noIndex: true, // noindex when disabled
  },
  systemCheck: {
    title: 'System Check | Canadian Document Services',
    description: 'Internal system compliance and security verification.',
    canonical: '/system-check',
    noIndex: true,
  },
};

/**
 * Organization Schema (JSON-LD)
 */
export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Canadian Document Services Inc.',
  url: 'https://canadiandocumentservices.com',
  logo: 'https://canadiandocumentservices.com/logo.png',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-866-554-3535',
    contactType: 'customer service',
    areaServed: 'CA',
    availableLanguage: 'English',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '5000 Yonge St. Suite 1901',
    addressLocality: 'North York',
    addressRegion: 'ON',
    postalCode: 'M2N 7E9',
    addressCountry: 'CA',
  },
  sameAs: [],
};

/**
 * Professional Service Schema (JSON-LD)
 */
export const PROFESSIONAL_SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'Canadian Document Services Inc.',
  description: 'Administrative assistance for government application processing including Record Suspension, U.S. Entry Waivers, and International Filings.',
  url: 'https://canadiandocumentservices.com',
  telephone: '+1-866-554-3535',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '5000 Yonge St. Suite 1901',
    addressLocality: 'North York',
    addressRegion: 'ON',
    postalCode: 'M2N 7E9',
    addressCountry: 'CA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 43.7615,
    longitude: -79.4111,
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '09:00',
    closes: '17:00',
  },
  areaServed: {
    '@type': 'Country',
    name: 'Canada',
  },
  serviceType: [
    'Record Suspension Applications',
    'U.S. Entry Waiver Applications',
    'International Document Authentication',
  ],
};

/**
 * Generate FAQ Schema from array of Q&A
 * @param {Array<{question: string, answer: string}>} faqs
 */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

/**
 * Generate BreadcrumbList Schema
 * @param {Array<{name: string, url: string}>} items
 */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_CONFIG.siteUrl}${item.url}`,
  })),
});

export default {
  SITE_CONFIG,
  PAGE_SEO,
  ORGANIZATION_SCHEMA,
  PROFESSIONAL_SERVICE_SCHEMA,
  generateFAQSchema,
  generateBreadcrumbSchema,
};
